import type {
  DrilldownParams,
  ExecutionsHistoryErrorCode,
  ExecutionsHistoryQuery,
  ExecutionsHistoryScheduleQuery,
  ExecutionsHistoryScheduleSection,
  ExecutionsHistoryRouteQuery,
  ExecutionsHistoryRouteSection,
  ExecutionsHistoryOperationQuery,
  ExecutionsHistoryOperationSection,
  RouteDrilldownQuery,
  RouteDrilldownSectionData,
  ScheduleDrilldownQuery,
  ScheduleDrilldownSectionData,
  ExecutionsHistorySection,
  ExecutionsHistorySectionRequest,
  ExecutionsHistorySectionResult,
  ReportEventDrilldownData,
  ReportEventDrilldownRequest,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ExecutionsHistoryDashboardRepository from '@src/repositories/ExecutionsHistoryDashboardRepository';
import { isInFlightLimitError } from '@src/util/cloud-functions/callableDomainCode';
import { sendErrorToSentry } from '@src/util/sentry/utils';

/** Sections whose series the backend groups by `interval`. */
export const SECTIONS_WITH_SERIES: ReadonlySet<ExecutionsHistorySection> = new Set<ExecutionsHistorySection>([
  'outcomes',
  'incidents-times',
  'schedule-overview',
  'route-overview',
  'operation-evolution',
]);

/** Callable error codes the report explains to the user; anything else is unexpected. */
const EXPECTED_ERROR_CODES: Record<string, ExecutionsHistoryErrorCode> = {
  'functions/deadline-exceeded': 'TIMEOUT',
  'functions/unimplemented': 'NOT_IMPLEMENTED',
};

const getExpectedErrorCode = (error: unknown): ExecutionsHistoryErrorCode | undefined => {
  const code = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined;
  if (isInFlightLimitError(error)) return 'IN_FLIGHT_LIMIT';
  return typeof code === 'string' ? EXPECTED_ERROR_CODES[code] : undefined;
};

const buildRequest = (
  section: ExecutionsHistorySection,
  { interval, ...filters }: ExecutionsHistoryQuery,
  drilldown?: DrilldownParams,
): ExecutionsHistorySectionRequest => {
  if (SECTIONS_WITH_SERIES.has(section)) return { ...filters, interval, section };
  if (section === 'drilldown' && drilldown) return { ...filters, ...drilldown, section };
  return { ...filters, section };
};

class ExecutionsHistoryDashboardService {
  private repository: ExecutionsHistoryDashboardRepository;

  constructor() {
    this.repository = new ExecutionsHistoryDashboardRepository();
  }

  /** Loads one report section; failures are returned, never thrown, so sections stay isolated. */
  async getSection<TData = unknown>(
    section: ExecutionsHistorySection,
    query: ExecutionsHistoryQuery,
    drilldown?: DrilldownParams,
  ): Promise<ExecutionsHistorySectionResult<TData>> {
    try {
      const response = await this.repository.getSection<TData>(buildRequest(section, query, drilldown));
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }

  /** Loads one schedule-report section using the same callable and response envelope as Tasks. */
  async getScheduleSection<TData = unknown>(
    section: ExecutionsHistoryScheduleSection,
    { interval, ...filters }: ExecutionsHistoryScheduleQuery,
  ): Promise<ExecutionsHistorySectionResult<TData>> {
    const request = (section === 'schedule-overview'
      ? { ...filters, interval, section }
      : { ...filters, section }) as ExecutionsHistorySectionRequest;
    try {
      const response = await this.repository.getSection<TData>(request);
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }

  /** Loads one checkpoint route report section using the shared callable. */
  async getRouteSection<TData = unknown>(
    section: ExecutionsHistoryRouteSection,
    { interval, ...filters }: ExecutionsHistoryRouteQuery,
  ): Promise<ExecutionsHistorySectionResult<TData>> {
    const request = (section === 'route-overview'
      ? { ...filters, interval, section }
      : { ...filters, section }) as ExecutionsHistorySectionRequest;
    try {
      const response = await this.repository.getSection<TData>(request);
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }

  /** Loads one Operations report section using the shared callable and response envelope. */
  async getOperationSection<TData = unknown>(
    section: ExecutionsHistoryOperationSection,
    query: ExecutionsHistoryOperationQuery,
  ): Promise<ExecutionsHistorySectionResult<TData>> {
    const request = { ...query, section } as ExecutionsHistorySectionRequest;
    try {
      const response = await this.repository.getSection<TData>(request);
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }

  /** Loads one page of schedule, event type or user rows behind a schedule report chart. */
  async getScheduleDrilldown(
    { interval: _interval, ...query }: ScheduleDrilldownQuery,
  ): Promise<ExecutionsHistorySectionResult<ScheduleDrilldownSectionData>> {
    const { drilldown, page, pageSize, search, ...filters } = query;
    const request: ExecutionsHistorySectionRequest = {
      ...filters,
      section: 'schedule-drilldown',
      drilldown,
      page,
      pageSize,
      ...(search ? { search } : {}),
    };
    try {
      const response = await this.repository.getSection<ScheduleDrilldownSectionData>(request);
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section: 'schedule-drilldown' });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }

  /** Loads one page of route, event type or user rows behind a route report chart. */
  async getRouteDrilldown(
    { interval: _interval, ...query }: RouteDrilldownQuery,
  ): Promise<ExecutionsHistorySectionResult<RouteDrilldownSectionData>> {
    const { drilldown, page, pageSize, search, ...filters } = query;
    const request: ExecutionsHistorySectionRequest = {
      ...filters,
      section: 'route-drilldown',
      drilldown,
      page,
      pageSize,
      ...(search ? { search } : {}),
    };
    try {
      const response = await this.repository.getSection<RouteDrilldownSectionData>(request);
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section: 'route-drilldown' });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }

  /** One page of events from the exact execution bucket behind a report detail row. */
  async getReportEventDrilldown(
    request: ReportEventDrilldownRequest,
  ): Promise<ExecutionsHistorySectionResult<ReportEventDrilldownData>> {
    try {
      const response = await this.repository.getSection<ReportEventDrilldownData>(request);
      return { status: 'ok', response };
    } catch (error) {
      const expectedErrorCode = getExpectedErrorCode(error);
      if (expectedErrorCode) return { status: 'error', errorCode: expectedErrorCode };
      sendErrorToSentry(error, { section: request.section });
      return { status: 'error', errorCode: 'UNKNOWN' };
    }
  }
}

export default ExecutionsHistoryDashboardService;
